/**
 * The single-use invite token a nurse invite e-mail carries in its
 * /#/verifyNurse link. The page has no session, so keyon lets the nurse's
 * Nursys lookup and enrolment through on its presence (tswebhook nursysGuard)
 * and authorizes her credential re-uploads and the finalize call by it; the
 * page sends it back in the X-Pacmny-Invite header (utils/inviteHeader.js),
 * never in a body, and it is never logged.
 *
 * It lives in the hash fragment, so it never reaches a server log or a Referer.
 * The page strips it from the visible URL on arrival.
 *
 * WHY IT IS KEPT FOR THE TAB. It used to live only in memory, so a refresh -
 * after the strip, the address bar no longer has it - sent every later call
 * without the header. keyon's gate answered 401 and the page, which had no
 * rejection handling, spun for good (dev, 2026-09-21). It is now kept in
 * sessionStorage: this tab only, gone when the tab closes, never sent anywhere
 * by the browser, and readable only by script on this origin, which could read
 * the in-memory copy just as well. It is keyed by the pending account, so a
 * second invite opened in the same tab is not handed this one, and it is
 * dropped once the finalize has used it up or keyon says it is spent.
 *
 * Every storage call is wrapped: private browsing and blocked site data make
 * sessionStorage throw, and the page must still work from memory then.
 */

// keyon/lib/InviteToken.php PATTERN: 64 lowercase hex.
const TOKEN_PATTERN = /^[a-f0-9]{64}$/;

const STORAGE_PREFIX = 'pacmny.verifyNurse.invite.';

/** The query's `invite`, or null unless it is 64 lowercase hex. */
export function readInviteToken(query) {
  const value = query && typeof query === 'object' ? query.invite : undefined;
  // `$` also matches before a trailing newline, so pin the length as well.
  if (typeof value !== 'string' || value.length !== 64 || !TOKEN_PATTERN.test(value)) return null;
  return value;
}

/** A shallow copy of the query without `invite`; the input is not changed. */
export function queryWithoutInvite(query) {
  const copy = { ...(query && typeof query === 'object' ? query : {}) };
  delete copy.invite;
  return copy;
}

function sessionStore() {
  try {
    return typeof window !== 'undefined' && window.sessionStorage ? window.sessionStorage : null;
  } catch (e) {
    return null;
  }
}

function storageKey(unique) {
  return typeof unique === 'string' && unique !== '' ? STORAGE_PREFIX + unique : null;
}

/**
 * Keep a well-formed token for this tab, keyed by the pending account's
 * unique id (the page's `unique` prop).
 * @returns {boolean} whether it was kept
 */
export function rememberInviteToken(unique, token, storage = sessionStore()) {
  const key = storageKey(unique);
  const value = readInviteToken({ invite: token });
  if (!key || !value || !storage) return false;
  try {
    storage.setItem(key, value);
    return true;
  } catch (e) {
    return false;
  }
}

/** The token this tab kept for the pending account, or null. */
export function recallInviteToken(unique, storage = sessionStore()) {
  const key = storageKey(unique);
  if (!key || !storage) return null;
  try {
    return readInviteToken({ invite: storage.getItem(key) });
  } catch (e) {
    return null;
  }
}

/** Drop the token this tab kept for the pending account, if any. */
export function forgetInviteToken(unique, storage = sessionStore()) {
  const key = storageKey(unique);
  if (!key || !storage) return;
  try {
    storage.removeItem(key);
  } catch (e) {
    // Nothing was kept, or storage cannot be reached; either way none remains.
  }
}

/**
 * The token a page load should use: the link's, which is then kept for this
 * tab, or failing that the one this tab kept on an earlier load.
 */
export function inviteTokenOnArrival(unique, invite, storage = sessionStore()) {
  const fromLink = readInviteToken({ invite });
  if (fromLink) {
    rememberInviteToken(unique, fromLink, storage);
    return fromLink;
  }
  return recallInviteToken(unique, storage);
}
